import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 91视频网站资讯`;
    } else {
      document.title = '文章 - 91视频网站';
    }
    window.scrollTo(0, 0);
  }, [slug, article]);

  if (!article) {
    return (
      <main className="pt-20 px-4 md:px-6 lg:px-8 min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-3">文章未找到</h1>
          <p className="text-foreground-500 mb-6">您访问的文章不存在或已被删除。</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600 transition-colors">
            <i className="ri-arrow-left-line" />
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const breadcrumbItems = [
    { label: '资讯', href: '/article/ai-video-trends' },
    { label: article.title },
  ];

  const relatedArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      '@type': 'Organization',
      name: '91视频网站',
      url: 'http://ewqivah.cn/',
    },
    publisher: {
      '@type': 'Organization',
      name: '91视频网站',
      logo: {
        '@type': 'ImageObject',
        url: 'http://ewqivah.cn/logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://ewqivah.cn/article/${slug}`,
    },
  };

  return (
    <main>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />

      <section className="pt-24 md:pt-32 pb-8 md:pb-10 px-4 md:px-6 lg:px-8 bg-background-50">
        <div className="w-full max-w-4xl mx-auto">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-6">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-100 text-primary-800 text-xs font-medium rounded-full mb-4">
              {article.category}
            </span>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-5">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" />
                {article.views}次阅读
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" />
                阅读时间约 5 分钟
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-12 md:pb-16 px-4 md:px-6 lg:px-8 bg-background-50">
        <div className="w-full max-w-4xl mx-auto">
          <div className="rounded-2xl overflow-hidden mb-10">
            <img
              src={article.image}
              alt={article.title}
              className="w-full aspect-[16/9] object-cover"
              loading="eager"
            />
          </div>

          <article className="prose prose-lg max-w-none">
            <p className="text-foreground-700 text-base md:text-lg leading-relaxed mb-6">
              {article.content}
            </p>
            <p className="text-foreground-700 text-base md:text-lg leading-relaxed mb-6">
              {article.excerpt} 在当今数字化时代，视频内容已经成为人们获取信息和娱乐的主要方式之一。随着互联网技术的不断发展，视频平台的数量和质量都在不断提升，为用户提供了前所未有的丰富选择。
            </p>
            <p className="text-foreground-700 text-base md:text-lg leading-relaxed mb-6">
              我们致力于为用户提供最新、最全、最优质的视频内容导航服务。无论您是想观看最新上映的电影大片，还是追看热播的电视剧集，亦或是欣赏精彩的综艺节目和动漫作品，91视频网站都能满足您的需求。
            </p>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mt-8 mb-4">行业发展趋势分析</h2>
            <p className="text-foreground-700 text-base md:text-lg leading-relaxed mb-6">
              近年来，中国视频行业经历了快速的发展和变革。从早期的视频网站到如今的综合内容平台，行业格局正在不断重塑。短视频的崛起、长视频的精品化、直播行业的规范化，都在推动着整个生态系统的演进。
            </p>
            <p className="text-foreground-700 text-base md:text-lg leading-relaxed mb-6">
              技术的进步也为视频行业带来了新的机遇。5G网络的普及让高清视频流媒体成为可能，AI推荐算法让内容分发更加精准，而VR/AR技术则为沉浸式观影体验开辟了新的想象空间。
            </p>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mt-8 mb-4">如何选择优质视频内容</h2>
            <p className="text-foreground-700 text-base md:text-lg leading-relaxed mb-6">
              面对海量的视频内容，如何快速找到自己感兴趣的作品是一门学问。首先，明确自己的观影偏好是关键——是偏爱紧张刺激的动作片，还是温馨感人的爱情片？是喜欢烧脑的悬疑剧，还是轻松愉快的喜剧？
            </p>
            <p className="text-foreground-700 text-base md:text-lg leading-relaxed mb-6">
              其次，善用平台的筛选和推荐功能。91视频网站提供多维度的内容分类和智能推荐系统，可以根据您的观影历史和偏好，精准推荐符合您口味的视频内容。
            </p>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mt-8 mb-4">结语</h2>
            <p className="text-foreground-700 text-base md:text-lg leading-relaxed mb-6">
              视频内容的消费升级正在加速，用户对优质内容的需求也在不断提升。91视频网站将持续优化内容筛选机制，为用户带来更加精准、高效的视频发现体验。感谢您选择91视频网站，让我们一起探索精彩的视频世界！
            </p>
          </article>

          <div className="mt-10 pt-8 border-t border-background-200">
            <h3 className="text-lg font-bold text-foreground-950 mb-5">相关资讯</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedArticles.map((a) => (
                <Link
                  key={a.id}
                  to={`/article/${a.slug}`}
                  className="group flex gap-3 p-3 rounded-xl hover:bg-background-100 transition-colors"
                >
                  <img
                    src={a.image}
                    alt={a.title}
                    className="w-20 h-14 rounded-lg object-cover shrink-0"
                    loading="lazy"
                  />
                  <div>
                    <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {a.title}
                    </h4>
                    <span className="text-xs text-foreground-500 mt-1">{a.date}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}